import { useEffect, useMemo, useState } from 'react';
import { Task } from 'taskarian';
import { fromEmpty, fromNullable, just, Maybe, nothing } from 'maybeasy';
import { findLinkByT } from '../../../../../../../Links';
import * as Resourceable from '../../../../../../../Resourceable';
import { ExperienceResource, ExperienceDetailResource } from '../../Types';
import { warn } from '@execonline-inc/logging';

export interface CombinedExperience {
  basic: ExperienceResource;
  detail: Maybe<ExperienceDetailResource>;
}

interface State {
  loading: boolean;
  combinedExperiences: CombinedExperience[];
  error: Maybe<string>;
}

// Module-level cache for experience details to avoid re-fetching when navigating back
const experienceDetailCache = new Map<number, ExperienceDetailResource>();

const getCachedDetail = (id: number): Maybe<ExperienceDetailResource> =>
  fromNullable(experienceDetailCache.get(id));

const cacheDetail = (id: number, detail: ExperienceDetailResource): void => {
  experienceDetailCache.set(id, detail);
};

/**
 * Execute tasks in batches to avoid overwhelming the browser/server
 * @param tasks - Array of tasks to execute
 * @param batchSize - Number of tasks to execute simultaneously (default: 3)
 */
const batchedParallelize = <E, T>(
  tasks: ReadonlyArray<Task<E, T>>,
  batchSize = 3,
): Task<E[], T[]> => {
  if (tasks.length === 0) {
    return Task.succeed([]);
  }

  // Split tasks into batches
  const batches: Task<E, T>[][] = [];
  for (let i = 0; i < tasks.length; i += batchSize) {
    batches.push(tasks.slice(i, i + batchSize));
  }

  // Process each batch sequentially, but tasks within each batch in parallel
  return batches.reduce(
    (acc, batch) =>
      acc.andThen((prevResults) => {
        // Execute current batch in parallel
        return new Task<E[], T[]>((reject, resolve) => {
          const rejected: E[] = [];
          const resolved: T[] = [];

          const rejectTask = (e: E): void => {
            rejected.push(e);
            tryComplete();
          };
          const resolveTask = (t: T): void => {
            resolved.push(t);
            tryComplete();
          };

          const tryComplete = (): void => {
            if (rejected.length + resolved.length === batch.length) {
              fromEmpty(rejected).cata({
                Just: reject,
                Nothing: () => resolve([...prevResults, ...resolved]),
              });
            }
          };

          const cancels = batch.map((task) => task.fork(rejectTask, resolveTask));

          return () => cancels.forEach((fn) => fn());
        });
      }),
    Task.succeed<E[], T[]>([]),
  );
};

/**
 * Fetches detail data for multiple experiences in parallel
 * @param experiences - Array of basic experience resources
 * @returns State object with loading status, combined experiences, and error
 */
export function useFetchMultipleExperienceDetails(experiences: ExperienceResource[]): State {
  const [state, setState] = useState<State>({
    loading: true,
    combinedExperiences: [],
    error: nothing(),
  });

  // Create a stable dependency key based on experience IDs to prevent infinite loops
  // React's shallow comparison on arrays causes re-renders even with same contents
  const experienceIdsKey = useMemo(
    () => experiences.map((e) => e.payload.id).join(','),
    [experiences],
  );

  useEffect(() => {
    // Track whether this effect has been cancelled to prevent stale state updates
    let isCancelled = false;

    // If no experiences, return empty state
    if (experiences.length === 0) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- This is a data-fetching effect that resets state when inputs change
      setState({ loading: false, combinedExperiences: [], error: nothing() });
      return;
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- This is a data-fetching effect that resets state before fetching
    setState({ loading: true, combinedExperiences: [], error: nothing() });

    // Create a task for each experience to fetch its details
    // Each task always succeeds (graceful degradation)
    // Check cache first to avoid unnecessary network requests
    const fetchTasks: Task<never, CombinedExperience>[] = experiences.map((experience) => {
      const expId = experience.payload.id;
      const cached = getCachedDetail(expId);

      // Return cached result immediately if available
      if (cached.isJust()) {
        return Task.succeed<never, CombinedExperience>({
          basic: experience,
          detail: cached,
        });
      }

      // Fetch from server and cache the result
      return Task.succeed({})
        .andThen(() => findLinkByT({ rel: 'details' }, experience.links))
        .andThen((link) => Resourceable.fetch(link))
        .map(({ resource }): CombinedExperience => {
          cacheDetail(expId, resource);
          return {
            basic: experience,
            detail: just(resource),
          };
        })
        .orElse((err): Task<never, CombinedExperience> => {
          // Log the error but don't fail the entire batch
          warn(`Failed to fetch details for experience ${experience.payload.id}`, err);
          return Task.succeed({
            basic: experience,
            detail: nothing(),
          });
        });
    });

    // Execute tasks in batches of 3 to avoid overwhelming the browser/server
    const cancelFn = batchedParallelize(fetchTasks, 3).fork(
      (errors: unknown[]) => {
        if (isCancelled) return;
        // This should never happen since we handle all errors with orElse
        warn('Unexpected error in parallelize', errors);
        setState({
          loading: false,
          combinedExperiences: [],
          error: just('Failed to load program details'),
        });
      },
      (combined: CombinedExperience[]) => {
        if (isCancelled) return;
        setState({
          loading: false,
          combinedExperiences: combined,
          error: nothing(),
        });
      },
    );

    return () => {
      isCancelled = true;
      cancelFn();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceIdsKey]);

  return state;
}
